/**
 * Fichiers servis sous Bearer (stratégie A, spec-tickets §7) : on lit les octets avec le client
 * authentifié, on n'expose en `blob:` que des IMAGES (affichées dans `<img>`), on télécharge le reste.
 */
import { client } from '@/api/generated/client.gen';
import { fetchAuthenticatedBlob, saveBlob } from '@/lib/files';

const FILES_PREFIX = '/api/files/';
const ATTACHMENT_DOWNLOAD = /^\/api\/attachments\/[\w-]+\/download$/;
const TRACKER_DOCUMENT_DOWNLOAD = /^\/api\/tracker\/documents\/[\w-]+\/download$/;
/** Contrôles et espaces : le parseur d'URL EFFACE TAB/LF/CR (`.\t.` redevient `..`) ; les clés S3 sont `[\w.-]`. */
const CONTROL_OR_SPACE = /[\u0000-\u0020\u007f]/;

/** Segment qu'un parseur d'URL effondrerait (`''`, `.`, `..`, y compris percent-encodés). */
function isUnsafeSegment(segment: string): boolean {
  const normalized = segment.toLowerCase().replace(/%2e/g, '.');
  return normalized === '' || normalized === '.' || normalized === '..';
}

/** URL relative servie par l'API sous Bearer : `/api/files/…`, `/api/attachments/<id>/download` ou `/api/tracker/documents/<id>/download`. */
export function isAuthenticatedApiUrl(url: string): boolean {
  if (CONTROL_OR_SPACE.test(url)) return false;
  if (ATTACHMENT_DOWNLOAD.test(url) || TRACKER_DOCUMENT_DOWNLOAD.test(url)) return true;
  if (!url.startsWith(FILES_PREFIX) || /[?#\\]/.test(url)) return false;
  return !url.slice(FILES_PREFIX.length).split('/').some(isUnsafeSegment);
}

/** GET authentifié par le client généré (Bearer, rejeu 401, ApiError) ; corps lu en Blob. */
export async function fetchApiBlob(url: string): Promise<{ blob: Blob; filename: string | null }> {
  // Garde avant tout envoi : une URL absolue partirait AVEC le Bearer vers une autre origine.
  if (!isAuthenticatedApiUrl(url)) throw new Error('Adresse de fichier non reconnue.');
  return fetchAuthenticatedBlob((options) => client.get<{ 200: Blob }, unknown, true>({
    url,
    ...options,
    throwOnError: true,
  }));
}

const objectUrls = new Map<string, Promise<string>>();
const createdObjectUrls = new Set<string>();
/** Incrémenté à chaque révocation : un chargement lancé avant ne doit pas repeupler le cache. */
let generation = 0;

/** Types d'image refusés en objectURL : un SVG ouvert en onglet s'exécuterait dans l'origine de la SPA. */
function isDisplayableImage(type: string): boolean {
  const mime = type.toLowerCase();
  return mime.startsWith('image/') && !mime.startsWith('image/svg');
}

async function loadObjectUrl(url: string, started: number): Promise<string> {
  const { blob } = await fetchApiBlob(url);
  if (!isDisplayableImage(blob.type)) throw new Error('Le fichier n\'est pas une image affichable.');
  const objectUrl = URL.createObjectURL(blob);
  if (started !== generation) {
    URL.revokeObjectURL(objectUrl);
    throw new Error('Session terminée.');
  }
  createdObjectUrls.add(objectUrl);
  return objectUrl;
}

/**
 * objectURL d'une IMAGE authentifiée, en cache par URL (Promise partagée entre appels concurrents).
 * Rejette — et retire l'entrée du cache — si l'URL n'est pas authentifiée, si la requête échoue ou si
 * le corps n'est pas une image.
 */
export function getAuthenticatedObjectUrl(url: string): Promise<string> {
  const cached = objectUrls.get(url);
  if (cached) return cached;
  if (!isAuthenticatedApiUrl(url)) return Promise.reject(new Error('Adresse de fichier non reconnue.'));

  const pending = loadObjectUrl(url, generation);
  objectUrls.set(url, pending);
  pending.catch(() => {
    if (objectUrls.get(url) === pending) objectUrls.delete(url);
  });
  return pending;
}

/** Révoque tous les objectURL du cache et le vide. */
export function revokeAuthenticatedObjectUrls(): void {
  generation += 1;
  createdObjectUrls.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  createdObjectUrls.clear();
  objectUrls.clear();
}

/** fetchApiBlob puis saveBlob(blob, filename ?? fallbackName). Rejette avec l'ApiError. */
export async function downloadFromApiUrl(url: string, fallbackName: string): Promise<void> {
  const { blob, filename } = await fetchApiBlob(url);
  saveBlob(blob, filename ?? fallbackName);
}
